import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { AlertCircle, List, SlidersHorizontal, SquareKanban } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useIssuesStore, useMentionsStore, usePMStore } from "@/stores/pm/hooks/usePMStore";
import * as pmRepo from "@/stores/pm/repository";
import { useProjects } from "@/domains/documents/hooks/useProjects";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationStore } from "@/hooks/useOrganizationStore";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { ChatLauncherSpacer } from "@/components/layout/ChatLauncherSpacer";
import { Button } from "@/components/ui/button";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import type { UnderlineTabOption } from "@/components/ui/underline-tabs";
import { PMEmptyState, PMLoadingState } from "@/domains/pm/components/shared";
import { IssuesList } from "@/domains/pm/components/issues/IssuesList";
import { useProjectResponsibilities } from "@/domains/contacts/useResponsibilities";
import { IssuesToolbar } from "@/domains/pm/components/issues/IssuesToolbar";
import { DEFAULT_ISSUE_SORT } from "@/domains/pm/components/issues/IssueSortControl";
import { IssueDetailPanel } from "@/domains/pm/components/issues/IssueDetailPanel";
import { usePMIssueLiveUpdates } from "@/domains/pm/hooks/usePMIssueLiveUpdates";
import { IssueFormDialog } from "@/domains/pm/components/issues/IssueFormDialog";
import { useNewIssueGuard } from "@/domains/pm/useNewIssueGuard";
import { IssueBulkActions } from "@/domains/pm/components/issues/IssueBulkActions";
import { KanbanBoard } from "@/domains/pm/components/issues/KanbanBoard";
import { useDocumentViewer } from "@/domains/documents/hooks/useDocumentViewer";
import { DocumentViewerDialog } from "@/domains/documents/components/DocumentViewerDialog";
import { ProjectsSection } from "@/domains/documents/components/ProjectsSection";
import {
  issueFiltersStorageKey,
  usePersistedIssueFilters,
} from "@/domains/pm/hooks/usePersistedIssueFilters";
import { selectedIssueParam } from "@/domains/pm/utils/deepLink";
import { useOpenIssueDeepLink } from "@/domains/pm/hooks/useOpenIssueDeepLink";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import type { PMIssue, PMIssueFilters, IssueStatus } from "@/domains/pm/types";
import type { IssueGroupBy, IssueSort, IssueSortField } from "@/domains/pm/utils/groupAndSort";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { isOpenStatus, visibleStatusKeys } from "@/domains/pm/statuses/statusCatalogue";
import { isIssueOverdue, overdueDueBefore } from "@/domains/pm/utils/dueDate";
import { useMyOpenAssignedIssues } from "@/domains/pm/hooks/useMyOpenAssignedIssues";
import { PageTitle } from "@/components/ui/page-title";
import { PageSubtitle } from "@/components/ui/page-subtitle";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

/**
 * WHA-3644 — "My Issues": a personal, building-scoped view of issues that
 * concern the current user. Three tabs replace the status tabs of the main
 * /issues page; each one applies a LOCKED server filter that can't be removed:
 *   - Assigned: issues where the user is an assignee.
 *   - Created:  issues the user created (created_by).
 *   - Mentions: issues where the user is @mentioned in any comment.
 * Everything else (project filter, discipline/docs/comments filters, group-by,
 * search, List + Kanban, the detail panel, bulk actions) mirrors PMIssues and
 * reuses the same sub-components and singleton IssuesStore.
 */
const TAB_ASSIGNED = 0;
const TAB_CREATED = 1;
const TAB_MENTIONS = 2;
// Overdue: the user's own overdue work. Uses the Assigned scope plus a
// server-side overdue narrowing (in_progress + due before today), so the tab
// reads only the overdue issues rather than every issue assigned to the user.
const TAB_OVERDUE = 3;

/** The locked "person" filter each tab applies (and the scope baseline). */
function tabPersonFilter(tab: number, uid: number): PMIssueFilters {
  if (tab === TAB_CREATED) return { createdByUserIds: [uid] };
  if (tab === TAB_MENTIONS) return { mentionedUserId: uid };
  if (tab === TAB_OVERDUE) {
    // The server-side form of isIssueOverdue, on the Assigned scope.
    return { assigneeUserIds: [uid], status: ["in_progress"], dueBefore: overdueDueBefore() };
  }
  return { assigneeUserIds: [uid] };
}

const PMMyIssues = observer(function PMMyIssues() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const deepLinkIssueId = selectedIssueParam(searchParams);
  const { t } = useLanguage();
  const { catalogue: statusCatalogue, isLoading: statusCatalogueLoading } = useStatusCatalogue();
  const issuesStore = useIssuesStore();
  const mentionsStore = useMentionsStore();
  const pmStore = usePMStore();
  const { userProfile } = useAuth();
  const orgStore = useOrganizationStore();
  const { currentOrganization } = useOrganizationContext();

  const { projects, isLoading: projectsLoading } = useProjects(id ?? "");
  // The project this page is scoped to. /my-issues is a Projects-view page:
  // BuildingLayout renders it only once a project is selected, so null is
  // only the transient before the layout settles, which useNewIssueGuard
  // refuses. Drives the issue project filter and where a new issue is filed.
  const { selectedProjectId } = useBuildingScope(id ?? "");
  // The statuses a remembered status filter may still name in this project.
  const myStatusKeys = useMemo(
    () => visibleStatusKeys(statusCatalogue, selectedProjectId ?? null),
    [statusCatalogue, selectedProjectId]
  );
  // New Issue files into the scoped project and nowhere else (the form has no
  // project choice), and refuses with a message rather than opening a form the
  // member could not save there (see useNewIssueGuard).
  const guardCreateIssue = useNewIssueGuard();
  // "My disciplines": the viewer's Responsibilities in this project (Contacts).
  const { myDisciplineIds } = useProjectResponsibilities(
    id ? Number(id) : null,
    selectedProjectId ?? null
  );
  // Gate the load/subscribe effects on projects existing — zero projects shows
  // the "create first project" empty state instead of querying issues.
  const issuesReady = !!id && !projectsLoading && projects.length > 0;
  // One viewer for the whole page — shared by row chips, kanban cards, and
  // the detail panel so a single DocumentViewerDialog lives in the tree.
  const viewer = useDocumentViewer();

  // Overdue-tab badge count: the user's open assigned issues that are past
  // due. Sourced from a dedicated, project-scoped query (not the active tab's
  // loaded list) so the badge stays correct on every tab, mirroring the "My
  // tasks" widget's data.
  const { data: myOpenAssigned } = useMyOpenAssignedIssues(
    id ? Number(id) : 0,
    selectedProjectId ?? null,
    userProfile?.id
  );
  const overdueCount = (myOpenAssigned ?? []).filter((issue) =>
    isIssueOverdue(issue, (status) =>
      isOpenStatus(statusCatalogue, issue.building_project_id, status)
    )
  ).length;

  const [viewMode, setViewMode] = useState<"list" | "kanban">("list");
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  // Status the New Issue form opens on — set by the per-column kanban "add
  // card"; undefined (header button) falls back to the form's default.
  const [formDefaultStatus, setFormDefaultStatus] = useState<IssueStatus | undefined>(undefined);
  // 0 = Assigned, 1 = Created, 2 = Mentions.
  const [activeTab, setActiveTab] = useState(TAB_ASSIGNED);
  const [hasLoaded, setHasLoaded] = useState(false);
  // Count of the user's issues for the active tab ignoring project/search/
  // discipline filters — the baseline for "N hidden by filters".
  const [scopeCount, setScopeCount] = useState(0);
  const [selectedIssueIds, setSelectedIssueIds] = useState<Set<number>>(new Set());
  const clearMultiSelect = () => setSelectedIssueIds(new Set());
  const [groupBy, setGroupBy] = useState<IssueGroupBy>(DEFAULT_ISSUE_SORT);
  const [sort, setSort] = useState<IssueSort>({ field: "issue_number", direction: "desc" });
  // Remembered per user, building and project, so leaving the page and coming
  // back (or reloading) keeps the list narrowed the way the user left it. The
  // hook also owns the search debounce, so a project switch swaps the search
  // in the same render as the filters.
  const {
    filter: filterValue,
    setFilter: setFilterValue,
    search: searchInput,
    setSearch: setSearchInput,
    debouncedSearch,
    clearAll: clearFiltersAndSearch,
  } = usePersistedIssueFilters(
    issueFiltersStorageKey("my-issues", userProfile?.id, id, selectedProjectId),
    {
      knownStatuses: statusCatalogueLoading ? null : myStatusKeys,
      // A deep link to one issue opens on the whole list, not behind the
      // filters last left here; see the hook.
      deepLink: deepLinkIssueId === null ? null : String(deepLinkIssueId),
    }
  );
  // The ?selected= value already opened: an in-app link to another issue
  // arrives without a remount and must open too.

  const orgUsers = orgStore.users.map((u) => ({
    id: u.id,
    first_name: u.first_name,
    last_name: u.last_name,
    email: u.email,
    avatar_url: u.avatar_url,
  }));

  const handleSortChange = (field: IssueSortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { field, direction: "asc" }
    );
  };

  // Guards the detail panel so a selection carried over from /issues (the
  // IssuesStore is a singleton shared between the two routes) can't render the
  // panel before this page has cleared it. Without this gate the destination
  // page renders the panel for the stale selection on its very first frame.
  // Flipped true
  // only AFTER the mount clear, so the panel shows only this page's own picks.
  const [selectionReady, setSelectionReady] = useState(false);
  useEffect(() => {
    issuesStore.clearSelection();
    setSelectionReady(true);
    return () => issuesStore.clearSelection();
  }, [issuesStore]);

  // Setup: disciplines and the mentions wiring. The filter application + fetch
  // live in a separate effect so they re-run on every filter/tab change.
  const userId = userProfile?.id ?? 0;
  useEffect(() => {
    if (!issuesReady || !id) return;
    pmStore.loadDisciplines();
    mentionsStore.setContext(Number(id), userId);
    mentionsStore.refresh();
  }, [id, issuesReady, pmStore, mentionsStore, userId]);

  // Realtime and the chatbot's writes: the list, the open issue and its
  // dependencies follow without a refresh.
  const onDragActiveChange = usePMIssueLiveUpdates(Number(id), {
    enabled: issuesReady && !!id,
    reload: () => void issuesStore.loadIssues(Number(id), userId),
    onChatbotWrite: () => mentionsStore.refresh(),
  });

  useEffect(() => {
    if (currentOrganization?.id) {
      orgStore.fetchOrganizationUsers(currentOrganization.id);
    }
  }, [currentOrganization?.id, orgStore]);

  // Push project + filter + search + the active tab's LOCKED person filter
  // into the store and refetch. Re-runs whenever any of them change.
  useEffect(() => {
    if (!issuesReady || !id) return;
    const numId = Number(id);
    const uid = userId;
    if (!uid) return;
    setHasLoaded(false);
    const base: PMIssueFilters = {
      buildingProjectId: selectedProjectId ?? undefined,
      disciplineIds: filterValue.disciplineIds.length ? filterValue.disciplineIds : undefined,
      hasLinkedDocuments: filterValue.hasLinkedDocuments,
      hasComments: filterValue.hasComments,
      search: debouncedSearch.trim() || undefined,
      // Status is a filter dimension here too. Overdue is NOT: this page's
      // Overdue tab owns that predicate (the dimension is hidden), and the tab
      // supplies its own status/dueBefore below.
      status: filterValue.status.length ? filterValue.status : undefined,
    };
    const tabFilter = tabPersonFilter(activeTab, uid);
    // The tab wins: Overdue pins status to in_progress, so its own value
    // replaces any status the user picked. The control hides the status
    // dimension on that tab (see hiddenDimensions below) so the pinning is
    // never a filter the UI claims but the query ignores.
    issuesStore.setFilters({ ...base, ...tabFilter });
    // Load the filtered list and the unfiltered tab scope together so the
    // "N hidden" count is consistent the moment the content renders.
    Promise.all([
      issuesStore.loadIssues(numId, uid),
      pmRepo
        .countIssuesMatching(numId, tabFilter, selectedProjectId ?? undefined)
        .then((n) => setScopeCount(n))
        .catch(() => setScopeCount(0)),
    ]).finally(() => setHasLoaded(true));
    // Stop the background pager when the tab or scope changes, or the page
    // goes away, rather than reading on against a list nobody is looking at.
    return () => issuesStore.cancelLoad();
  }, [
    id,
    issuesReady,
    selectedProjectId,
    filterValue,
    debouncedSearch,
    activeTab,
    issuesStore,
    userId,
  ]);

  useOpenIssueDeepLink(deepLinkIssueId, hasLoaded, issuesStore);

  // Keyed by index as strings — the tab identity here IS the position (see
  // TAB_ASSIGNED..TAB_OVERDUE), and the strip wants string values. Only Overdue
  // carries a count, in the "alert" tone that paints it red whichever tab is
  // active, since an overdue count is a warning rather than a selection cue.
  const tabOptions: Array<UnderlineTabOption<string>> = [
    t.pm.myIssues.tabs.assigned,
    t.pm.myIssues.tabs.created,
    t.pm.myIssues.tabs.mentions,
    t.pm.issues.tabs.overdue,
  ].map((label, i) => ({
    value: String(i),
    label,
    ...(i === TAB_OVERDUE ? { count: overdueCount, countTone: "alert" as const } : {}),
  }));

  const handleTabChange = (i: number) => {
    if (i === activeTab) return;
    issuesStore.clearSelection();
    clearMultiSelect();
    setActiveTab(i);
  };

  const handleSelectIssue = (issue: PMIssue) => {
    if (issuesStore.selectedIssue?.id === issue.id) {
      issuesStore.clearSelection();
    } else {
      issuesStore.selectIssue(issue.id);
    }
  };

  const handleJumpToIssue = (issueId: number) => issuesStore.selectIssue(issueId);

  // Header button: open the form with no preset status.
  const openNewIssueForm = () =>
    guardCreateIssue(selectedProjectId ?? null, () => {
      setFormDefaultStatus(undefined);
      setIsFormDialogOpen(true);
    });

  // Per-column kanban "add card": open the form pre-set to that status.
  const handleAddIssueInStatus = (status: IssueStatus) =>
    guardCreateIssue(selectedProjectId ?? null, () => {
      setFormDefaultStatus(status);
      setIsFormDialogOpen(true);
    });

  // How many of the user's issues (for this tab) the project/search/discipline
  // filters currently hide. Drives the "N issues hidden by filters" banner —
  // shown both above a populated list and in the empty state. The scope count
  // carries the tab's own narrowing (including Overdue), so only user-applied
  // filters can ever move this number.
  const isOverdueTab = activeTab === TAB_OVERDUE;
  const hidden = Math.max(0, scopeCount - issuesStore.totalCount);
  const hasVisibleRows = issuesStore.issues.length > 0;
  // Overdue is a list-only filter (the kanban lays issues out by status column,
  // and overdue issues are all in_progress) — force the list view for it.
  const effectiveViewMode = isOverdueTab ? "list" : viewMode;
  const clearAllFilters = clearFiltersAndSearch;
  const emptyCopy = isOverdueTab
    ? t.pm.myIssues.empty.overdue
    : activeTab === TAB_CREATED
      ? t.pm.myIssues.empty.created
      : activeTab === TAB_MENTIONS
        ? t.pm.myIssues.empty.mentions
        : t.pm.myIssues.empty.assigned;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-7 pt-4">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <PageTitle info="myIssues">{t.pm.myIssues.title}</PageTitle>
            <PageSubtitle className="mt-1">{t.pm.myIssues.subtitle}</PageSubtitle>
          </div>
          <div className="flex items-center gap-2">
            <div {...pageInfoTarget("myIssues.layout")}>
              <SegmentedControl
                value={viewMode}
                // See the same note in PMIssues: the bare setter makes the
                // generic infer as `string`.
                onValueChange={(v: "list" | "kanban") => setViewMode(v)}
                options={[
                  { value: "list", label: t.pm.issues.listView, icon: <List /> },
                  { value: "kanban", label: t.pm.issues.kanbanView, icon: <SquareKanban /> },
                ]}
              />
            </div>
            <Button
              size="control"
              onClick={openNewIssueForm}
              {...pageInfoTarget("issues.newIssue")}
            >
              + {t.pm.issues.newIssue}
            </Button>
          </div>
        </div>

        {/* Tabs and the toolbar share one row, tabs left and toolbar right —
            see the same block in PMIssues for why the toolbar is the flexible
            half and the tabs take shrink-0.

            Assigned / Created / Mentions / Overdue show in BOTH views (unlike
            the Issues page's status tabs) since they filter the dataset rather
            than by status, so the row never loses its left half here. The
            toolbar hides the assignee filter dimension: the active tab defines
            the person. */}
        <div className="flex items-end gap-4">
          <UnderlineTabs
            className="-mb-px shrink-0"
            value={String(activeTab)}
            onValueChange={(value) => handleTabChange(Number(value))}
            options={tabOptions}
            infoTarget="myIssues.tabs"
          />
          <IssuesToolbar
            className="min-w-0 flex-1 pb-1.5"
            users={orgUsers}
            disciplines={pmStore.disciplines}
            filter={{
              value: filterValue,
              onChange: setFilterValue,
              // assignee: this page's tabs already lock the person
              // relationship (assigned / created / mentioned).
              // overdue: its Overdue tab owns that predicate — two controls
              // for one filter would fight.
              // status: hidden on the Overdue tab only, which pins it to
              // in_progress (tabPersonFilter wins the spread below). Left
              // visible it would keep offering a choice that the tab silently
              // overrides, and chip a filter that isn't being applied.
              hiddenDimensions: isOverdueTab
                ? ["assignee", "overdue", "status"]
                : ["assignee", "overdue"],
            }}
            sort={{ value: groupBy, onChange: setGroupBy, show: effectiveViewMode === "list" }}
            search={{ value: searchInput, onChange: setSearchInput }}
            myDisciplineIds={myDisciplineIds}
          />
        </div>
      </div>

      {/* Content. The detail panel is a fixed overlay (WHA-3644) that floats
          on top of the list/board, so the content keeps full width. */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto pt-3">
          {issuesStore.error && (
            <div
              role="alert"
              className="mx-7 mb-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{issuesStore.error}</span>
            </div>
          )}
          {projectsLoading ? (
            <PMLoadingState />
          ) : projects.length === 0 && id ? (
            <div className="px-7 pb-6 pt-3">
              <ProjectsSection
                buildingId={id}
                selectedProject={null}
                onProjectSelect={() => {
                  // No-op — once a project is created useProjects refetches
                  // and this branch unmounts.
                }}
              />
            </div>
          ) : !hasLoaded ? (
            // Gate on the FIRST load so a stale list (from /issues, same
            // singleton store) or the previous tab never flashes.
            <PMLoadingState />
          ) : hasVisibleRows ? (
            <>
              {effectiveViewMode === "list" && (
                <IssuesList
                  onSelectIssue={handleSelectIssue}
                  selectedIssueId={issuesStore.selectedIssue?.id}
                  overdueOnly={isOverdueTab}
                  groupBy={groupBy}
                  sort={sort}
                  onSortChange={handleSortChange}
                  openViewer={viewer.openViewer}
                  selectedIssueIds={selectedIssueIds}
                  setSelectedIssueIds={setSelectedIssueIds}
                  hiddenCount={hidden}
                  onClearFilters={clearAllFilters}
                />
              )}
              {effectiveViewMode === "kanban" && (
                <KanbanBoard
                  buildingId={Number(id)}
                  openViewer={viewer.openViewer}
                  onSelectIssue={handleSelectIssue}
                  onAddIssue={handleAddIssueInStatus}
                  onDragActiveChange={onDragActiveChange}
                />
              )}
            </>
          ) : hidden > 0 ? (
            // The tab HAS issues, but the current project/filters hide them
            // all — say so (and offer a reset) instead of "you have none".
            !issuesStore.error && (
              <PMEmptyState
                icon={SlidersHorizontal}
                title={t.pm.issues.filterBy.hiddenByFilters.replace("{count}", String(hidden))}
                description={t.pm.myIssues.empty.filteredHint}
                actionLabel={t.pm.issues.filterBy.clearFilters}
                onAction={clearAllFilters}
              />
            )
          ) : (
            !issuesStore.error && (
              <PMEmptyState
                icon={AlertCircle}
                title={emptyCopy.title}
                description={emptyCopy.subtitle}
              />
            )
          )}
          <ChatLauncherSpacer />
        </div>

        {selectionReady && issuesStore.selectedIssue && (
          <IssueDetailPanel
            issue={issuesStore.selectedIssue}
            onClose={() => issuesStore.clearSelection()}
            openViewer={viewer.openViewer}
            onJumpToIssue={handleJumpToIssue}
          />
        )}
      </div>

      <DocumentViewerDialog
        document={viewer.document}
        blobUrl={viewer.blobUrl}
        blobData={viewer.blobData}
        open={viewer.isOpen}
        onOpenChange={viewer.setIsOpen}
        originalDwgFilePath={viewer.originalDwgFilePath ?? undefined}
      />

      {id && (
        <IssueFormDialog
          open={isFormDialogOpen}
          onOpenChange={setIsFormDialogOpen}
          buildingId={Number(id)}
          defaultProjectId={selectedProjectId != null ? String(selectedProjectId) : undefined}
          defaultStatus={formDefaultStatus}
        />
      )}

      {/* Floating bulk-action toolbar — only visible when ≥1 row checked. */}
      {id && effectiveViewMode === "list" && (
        <IssueBulkActions
          selectedIssueIds={Array.from(selectedIssueIds)}
          buildingId={Number(id)}
          onClear={clearMultiSelect}
        />
      )}
    </div>
  );
});

export default PMMyIssues;
